import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Plus, Pencil, Trash2, Play, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogClose,
} from "../../components/ui/dialog";
import {
  addReview,
  updateReview,
  getAllReviews,
  deleteReview,
} from "../../services/api"; // Import the API functions
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface Review {
  id: string;
  clientName: string;
  comment: string;
  media_path: string;
  ismedia_path?: boolean;
}

const Reviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]) as any;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [comment, setComment] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const MAX_COMMENT_LENGTH = 500;
  const charactersLeft = MAX_COMMENT_LENGTH - comment.length;

  // Fetch reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await getAllReviews();
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to fetch reviews.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      setSelectedFile(file);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!comment.trim()) {
      toast.error("Please fill in the comment field.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const formData = new FormData();
    formData.append("client_name", clientName.trim() || "Anonymous");
    formData.append("comment", comment.trim());
    if (selectedFile) {
      formData.append("media", selectedFile);
    }

    try {
      if (editingReview) {
        // Update existing review
        await updateReview(editingReview.id, formData);

        toast.success("Review updated successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        // Add new review
        await addReview(formData);
        toast.success("Review added successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      }

      // Refetch reviews after adding/updating
      await fetchReviews();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        editingReview ? "Failed to update review." : "Failed to add review.",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
    }
  };

  const resetForm = () => {
    setClientName("");
    setComment("");
    setSelectedFile(null);
    setPreviewUrl(null);
    setEditingReview(null);
  };

  const handleEditClick = (review: any) => {
    setClientName(review.client_name);
    setComment(review.comment);
    setPreviewUrl(review.media_url);
    setEditingReview(review);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    try {
      await deleteReview(id); // Call the delete API function
      toast.success("Review deleted successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
      await fetchReviews(); // Refetch reviews after deletion
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("Failed to delete review.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="p-8">
      <ToastContainer />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Reviews</h1>
          <p className="text-muted-foreground">
            Let's share what our clients are saying
          </p>
        </div>
        <Button
          className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
          onClick={() => setIsModalOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          ADD REVIEW
        </Button>
      </div>

      <Card className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div>
            {reviews.data?.map((review: any) => (
              <div
                key={review.id}
                className="flex items-center justify-between py-4 border-b last:border-b-0"
              >
                <div className="w-[80%] grid grid-cols-4 gap-4">
                  <div className="col-span-1 relative h-12 w-12 rounded-md overflow-hidden bg-muted">
                    <div className="col-span-1 relative h-12 w-12 rounded-md overflow-hidden bg-muted">
                      {review.media_url ? (
                        <div className="h-full w-full relative">
                          <img
                            src={review.media_url}
                            alt={review.client_name}
                            className="h-full w-full object-cover"
                          />
                          {[".mp4", ".mov", ".webm", ".avi"].some((ext) =>
                            review.media_url.toLowerCase().endsWith(ext)
                          ) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <Play className="h-6 w-6 text-white" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <img
                          src="/placeholder.svg"
                          alt={review.client_name}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 w-full">
                    <div className="text-sm text-muted-foreground">
                      Client's Name
                    </div>
                    <div className="font-medium">{review.client_name}</div>
                  </div>
                  <div className="max-w-md">
                    <div className="text-sm text-muted-foreground">Comment</div>
                    <div className="font-medium truncate">{review.comment}</div>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#B87775] hover:text-[#B87775] hover:bg-[#B87775]/10"
                      onClick={() => handleEditClick(review)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-500 hover:bg-red-50"
                      onClick={() => handleDeleteClick(review.id)} // Add delete handler
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[900px] p-0 overflow-hidden">
          <DialogHeader className="p-0">
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-6 w-6 text-[#B87775]" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {/* Left side - Upload area */}
            <div className="border rounded-md flex flex-col items-center justify-center p-6 bg-gray-50">
              {previewUrl ? (
                <div className="relative w-full h-64 mb-4">
                  {[".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp"].some(
                    (ext) => previewUrl.toLowerCase().endsWith(ext)
                  ) ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <video
                      src={previewUrl}
                      className="w-full h-full object-contain"
                      controls
                    />
                  )}
                </div>
              ) : (
                <div className="w-full h-64 mb-4 flex items-center justify-center bg-white border-2 border-dashed border-gray-300 rounded-md">
                  <div className="text-center">
                    <svg
                      width="100"
                      height="100"
                      viewBox="0 0 100 100"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mx-auto mb-4"
                    >
                      <rect
                        x="30"
                        y="20"
                        width="60"
                        height="60"
                        rx="4"
                        fill="#E5E5E5"
                      />
                      <rect
                        x="10"
                        y="30"
                        width="60"
                        height="60"
                        rx="4"
                        fill="#D9D9D9"
                      />
                      <path
                        d="M30 70L45 55L55 65L65 55L80 70"
                        stroke="#999999"
                        strokeWidth="2"
                      />
                      <circle cx="45" cy="45" r="5" fill="#999999" />
                    </svg>
                  </div>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,video/*"
                className="hidden"
              />

              <Button
                onClick={handleUploadClick}
                className="bg-[#B87775] hover:bg-[#B87775]/90 text-white uppercase"
              >
                Upload Image or Video
              </Button>
            </div>

            {/* Right side - Form */}
            <div className="flex flex-col space-y-6">
              <div>
                <label
                  htmlFor="clientName"
                  className="block text-sm font-medium mb-2"
                >
                  Client's Name (optional)
                </label>
                <Input
                  id="clientName"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="eg Yinka Olatunji"
                  className="w-full"
                />
              </div>

              <div>
                <label
                  htmlFor="comment"
                  className="block text-sm font-medium mb-2"
                >
                  Comment <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) =>
                    setComment(e.target.value.substring(0, MAX_COMMENT_LENGTH))
                  }
                  placeholder="eg My session with Charmé was beyond anything i imagined, she was so calm and professional"
                  className="w-full min-h-[180px] resize-none"
                  required
                />
                <div className="text-right text-sm text-muted-foreground mt-1">
                  {charactersLeft} Characters left
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 p-6 border-t">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsModalOpen(false);
              }}
              className="uppercase border-[#B87775] text-[#B87775]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!comment.trim()}
              className="bg-[#B87775] hover:bg-[#B87775]/90 text-white uppercase"
            >
              {editingReview ? "Update" : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reviews;
